import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Poppins } from 'src/common/Poppins';
import { MOTION_TRANSITION } from 'src/config';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  overflow: hidden;
`;

const Header = styled.button<{ $open: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 20px 16px;
  background: ${({ theme }) => theme.colors.white};
  border: none;
  cursor: pointer;
  transition: background 0.15s ease;
  ${({ theme, $open }) => $open && `border-bottom: 1px solid ${theme.colors.stroke};`}

  &:hover {
    background: ${({ theme }) => theme.colors.cultured};
  }
  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.gp1};
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleSvg = styled.svg`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.cflowerBlue};
`;

interface CollapsibleProps {
  icon: React.ReactNode;
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

export const Collapsible: React.FC<CollapsibleProps> = ({ icon, label, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card>
      <Header type="button" $open={open} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Left>
          {icon}
          <Poppins px={16} weight={500} color="prussianBlue">
            {label}
          </Poppins>
        </Left>
        <ToggleSvg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.91667 7H11.0833"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {!open && (
            <path
              d="M7 2.91667V11.0833"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </ToggleSvg>
      </Header>
      <motion.div
        initial={false}
        animate={{ height: open ? 'auto' : 0 }}
        transition={MOTION_TRANSITION}
        style={{ overflow: 'hidden' }}
      >
        {children}
      </motion.div>
    </Card>
  );
};
